/**
 * KNLS Typing Tutor — structured curriculum
 * Goals: DCA/TALLY = 35 WPM | DTP/PGDCA = 55 WPM
 * Inspired by Typing.com structure + Keybr adaptive drills + Ratatype clean tests
 */

import { buildFingeringLessons } from './fingeringCurriculum';

export const TT_GOALS = {
  lower: 35,
  higher: 55,
};

export const HIGHER_COURSES = ['PGDCA', 'PGDCA2', 'PGDCA_2', 'DTP'];

export const resolveTargetWpm = (enrolledCourses = []) => {
  const codes = (enrolledCourses || []).map((c) => String(c).toUpperCase());
  const isHigher = codes.some((c) => HIGHER_COURSES.some((h) => c.includes(h)));
  return isHigher ? TT_GOALS.higher : TT_GOALS.lower;
};

export const resolveGoalTierLabel = (enrolledCourses = []) => {
  const codes = (enrolledCourses || []).map((c) => String(c).toUpperCase());
  const hasPgda = codes.some((c) => ['PGDCA', 'PGDCA2', 'PGDCA_2'].some((h) => c.includes(h)));
  const hasDtp = codes.some((c) => c.includes('DTP'));
  if (hasPgda && hasDtp) return 'PGDCA/DTP';
  if (hasPgda) return 'PGDCA';
  if (hasDtp) return 'DTP';
  return 'DCA';
};

export const EXERCISE_TYPES = [
  {
    id: 'fingering',
    title: 'Fingering',
    subtitle: 'Home row & key drills',
    description: 'Classic key-by-key drills with on-screen keyboard and finger guide.',
    icon: 'keyboard',
    order: 1,
  },
  {
    id: 'words',
    title: 'Words',
    subtitle: 'Common word patterns',
    description: 'Short word drills to improve rhythm and accuracy.',
    icon: 'text_fields',
    order: 2,
  },
  {
    id: 'sentences',
    title: 'Sentences',
    subtitle: 'Single-line practice',
    description: 'Type complete sentences with punctuation.',
    icon: 'short_text',
    order: 3,
  },
  {
    id: 'paragraph',
    title: 'Paragraph Tests',
    subtitle: 'Timed paragraph',
    description: 'Longer passages — exam-style typing test.',
    icon: 'article',
    order: 4,
  },
  {
    id: 'stories',
    title: 'Stories',
    subtitle: 'Narrative passages',
    description: 'Classic practice stories plus notes and flash cards from your enrolled courses.',
    icon: 'menu_book',
    order: 5,
  },
];

const fingeringLessons = buildFingeringLessons();

const wordLessons = [
  { id: 'w1', title: 'Top 20 Words', text: 'the be to of and a in that have I it for not on with he as you do at' },
  { id: 'w2', title: 'Computer Terms', text: 'computer keyboard monitor mouse printer software hardware internet email file folder' },
  { id: 'w3', title: 'Office Words', text: 'document spreadsheet presentation table chart format print save open close edit copy paste' },
  { id: 'w4', title: 'Action Words', text: 'create delete update search find replace select insert remove upload download share send receive' },
  { id: 'w5', title: 'KNLS Practice', text: 'knls student course lesson practice speed accuracy typing certificate journey progress' },
  { id: 'w6', title: 'Speed Builder 1', text: 'time work learn skill improve daily practice focus goal achieve result success effort' },
  { id: 'w7', title: 'Speed Builder 2', text: 'quick fast slow steady rhythm finger hand eye mind train build grow excel master' },
  { id: 'w8', title: 'Mixed Drill', text: 'data entry operator office assistant computer fundamental windows word excel powerpoint' },
  { id: 'w9', title: 'Exam Words', text: 'examination result marks grade pass fail certificate diploma degree course duration' },
  { id: 'w10', title: 'Final Word Test', text: 'professional typing speed test accuracy matters more than raw speed at first practice daily' },
];

const sentenceLessons = [
  { id: 's1', title: 'Basic Sentences', text: 'The sun rises in the east. Birds fly in the sky. Water is essential for life.' },
  { id: 's2', title: 'Computer Basics', text: 'A computer has hardware and software. The CPU is the brain of the computer. RAM stores temporary data.' },
  { id: 's3', title: 'MS Word', text: 'MS Word is used to create documents. You can bold, italic, and underline text. Mail merge sends letters to many people.' },
  { id: 's4', title: 'MS Excel', text: 'Excel uses rows and columns. Formulas start with an equal sign. AutoSum adds numbers quickly.' },
  { id: 's5', title: 'Internet & Email', text: 'The internet connects computers worldwide. Email sends messages instantly. Always use a strong password.' },
  { id: 's6', title: 'Typing Tips', text: 'Keep your fingers on the home row. Look at the screen, not the keyboard. Practice daily for best results.' },
  { id: 's7', title: 'Office Practice', text: 'Arrive on time for class. Complete assignments before the deadline. Ask your teacher when you need help.' },
  { id: 's8', title: 'Sentence Test', text: 'Typing is a skill that improves with practice. Set a goal of thirty five words per minute for DCA students.' },
];

const paragraphLessons = [
  {
    id: 'p1',
    title: 'Paragraph Test 1',
    text: 'Learning to type is one of the most useful skills in the modern world. Whether you are writing emails, preparing documents, or taking online exams, fast and accurate typing saves time and reduces errors. Start by placing your fingers on the home row keys. Practice a little every day and your speed will grow steadily. Remember that accuracy is more important than speed when you are beginning.',
  },
  {
    id: 'p2',
    title: 'Paragraph Test 2',
    text: 'Microsoft Office includes Word for documents, Excel for spreadsheets, and PowerPoint for presentations. These tools are used in offices, schools, and businesses across India. A Diploma in Computer Applications teaches students how to use these programs effectively. Along with software skills, students also learn typing speed because data entry and document preparation require both accuracy and speed.',
  },
  {
    id: 'p3',
    title: 'Paragraph Test 3',
    text: 'The QWERTY keyboard layout was designed for typewriters and remains the standard on computers today. Each finger is responsible for specific keys. The left hand covers Q, W, E, R, T and A, S, D, F, G. The right hand covers Y, U, I, O, P and H, J, K, L. With regular practice, your fingers will learn where each key is without looking down.',
  },
  {
    id: 'p4',
    title: 'Exam Paragraph',
    text: 'In typing examinations, candidates are given a passage and a time limit. Words per minute is calculated by counting correctly typed characters and dividing by five, then by the number of minutes. Errors reduce the final score. Students preparing for DCA should aim for thirty five words per minute. DTP and PGDCA students should aim for fifty five words per minute. Daily practice on the KNLS typing tutor will help you reach these goals.',
  },
  {
    id: 'p5',
    title: 'Final Paragraph Test',
    text: 'Consistency beats intensity when learning any skill. Fifteen minutes of focused typing practice every day is better than a long session once a week. Track your words per minute and accuracy after each lesson. Celebrate small improvements. When you reach your course goal, continue practicing to maintain your speed. Good typing habits will help you throughout your career in any field that uses computers.',
  },
];

const storyLessons = [
  {
    id: 'st1',
    title: 'The Diligent Student',
    text: 'Ravi joined KNLS to learn computer applications. On the first day, his typing speed was only twelve words per minute. The teacher told him to practice fingering lessons daily. Ravi followed the typing tutor every morning before class. After one month, he reached twenty eight words per minute. After three months, he crossed thirty five words per minute and earned his TT Speed certificate. His classmates asked for his secret. Ravi smiled and said, "Practice every day without skipping."',
  },
  {
    id: 'st2',
    title: 'The Office Interview',
    text: 'Priya applied for a data entry job after completing her DCA course. The interviewer gave her a paragraph to type in five minutes. Because she had practiced on the KNLS typing tutor, she typed with confidence. Her speed was forty words per minute with ninety six percent accuracy. She got the job on the spot. Later she told her juniors that typing practice on the LMS site made the difference. Skills built during the course helped her career from day one.',
  },
  {
    id: 'st3',
    title: 'PGDCA Challenge',
    text: 'Arjun enrolled in PGDCA and needed fifty five words per minute for his advanced certificate. He started with fingering and word drills, then moved to paragraph tests. Some days his speed dropped, but he did not give up. He used the statistics page to track progress over weeks. Slowly his average climbed from thirty to forty, then forty five, and finally fifty seven. The typing tutor records synced to his mobile app so he could see his journey update after every session. Hard work and the right tools made his goal possible.',
  },
];

export const CURRICULUM = {
  fingering: fingeringLessons,
  words: wordLessons,
  sentences: sentenceLessons,
  paragraph: paragraphLessons,
  stories: storyLessons,
};

/** Original built-in stories — always shown first in Stories exercise */
export const DEFAULT_STORY_LESSONS = storyLessons;

export const getLesson = (exerciseType, lessonId) => {
  const lessons = CURRICULUM[exerciseType] || [];
  return lessons.find((l) => l.id === lessonId) || null;
};

export const getExerciseType = (exerciseTypeId) =>
  EXERCISE_TYPES.find((e) => e.id === exerciseTypeId) || null;
